import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Grid,
  TextField,
  Tooltip,
  createFilterOptions,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useCallback, useContext, useEffect, useState } from 'react'
import { Controller, FieldValues, useForm } from 'react-hook-form'
import DateFnsProvider from '../../../providers/DateFnsProvider'
import { SelectItem, getSelectItemOptionLabel, normalizeSelectItems, isSelectItemEqual } from '../../../types/SelectItem'
import { ListViewContext } from '../../../contexts/ListViewContext'
import { FilterControls } from './FilterControls'
import {
  useItemCollectionNumberOptionsLazyQuery,
  useItemCollectionTypesListQuery,
  useOriginNumberAutoCompleteOptionsLazyQuery,
} from '@kistill/client/data-access'
import {
  handleOnSubmit,
  OnFilterType,
} from '../../../hooks/useHandleOnSubmitFilters'
import { getHiddenFilterFields } from '../filterConfig'
import { useClearFiltersOnColumnHide } from '../../../hooks/useClearFiltersOnColumnHide'
import { useFilterSync } from '../../../hooks/useFilterSync'

interface ItemCollectionFilterProps {
  onFilter: OnFilterType
  selected: readonly string[]
  setSelected: (selected: Set<string>) => void
  columnVisibility?: { [key: string]: boolean }
  initialFilterParams?: Record<string, unknown>
}

export function ItemCollectionFilters({
  onFilter,
  selected,
  setSelected,
  columnVisibility = {},
  initialFilterParams,
}: ItemCollectionFilterProps) {
  // Get hidden filter fields based on column visibility
  const hiddenFields = getHiddenFilterFields('itemCollection', columnVisibility)
  const { handleSubmit, watch, control, register, setValue, reset } =
    useForm({ shouldUnregister: true })
  const { showOnlySelected, updateShowOnlySelected } =
    useContext(ListViewContext)
  const [inputValueItemCollection, setInputValueItemCollection] = useState('')
  const [inputValueOrigin, setInputValueOrigin] = useState('')
  const [itemCollectionOptions, setItemCollectionOptions] = useState<
    SelectItem[]
  >([])
  const [originOptions, setOriginOptions] = useState<SelectItem[]>([])
  const { data } = useItemCollectionTypesListQuery()
  const itemCollectionTypeOptions = data?.getCollectionTypesList as SelectItem[]

  const [getItemCollectionNumberOptions] =
    useItemCollectionNumberOptionsLazyQuery({
      variables: { query: inputValueItemCollection },
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        if (data.itemCollectionNumberOptions) {
          setItemCollectionOptions(
            data.itemCollectionNumberOptions.map((x) => {
              return { id: x?.id, title: x.title }
            }),
          )
        }
      },
    })

  const [getOriginNumberOptions] = useOriginNumberAutoCompleteOptionsLazyQuery({
    variables: { query: inputValueOrigin },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data.originNumberOptions) {
        setOriginOptions(
          data.originNumberOptions.map((x) => {
            return { id: x?.id, title: x.title }
          }),
        )
      }
    },
  })

  const onSubmit = useCallback(
    (data: FieldValues) => {
      handleOnSubmit(onFilter)(data)
    },
    [onFilter],
  )

  const { formStateRef, clearFilters, clearSpecificFields } = useFilterSync({
    initialFilterParams,
    reset,
    watch,
    onSubmit,
    getSyncKey: (normalized) =>
      normalized === null ? null : JSON.stringify(normalized),
    getClearSyncKey: () => 'itemCollection',
  })

  const handleInputChangeItemCollection = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueItemCollection(value)
  }

  const handleInputChangeOrigin = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueOrigin(value)
  }
  const filterOptions = createFilterOptions<SelectItem>({
    trim: true,
  })

  const deselectCheckboxChange = () => {
    setSelected(new Set())
    if (showOnlySelected) {
      setValue('selectedGuids', null)
      updateShowOnlySelected(!showOnlySelected)
      reset({})
    }
  }

  useEffect(() => {
    getItemCollectionNumberOptions()
  }, [getItemCollectionNumberOptions])

  useEffect(() => {
    getOriginNumberOptions()
  }, [getOriginNumberOptions])

  // Clear filter values when their corresponding columns are hidden
  useClearFiltersOnColumnHide(
    'itemCollection',
    columnVisibility,
    setValue,
    clearSpecificFields,
    () => {
      setInputValueOrigin('')
      setInputValueItemCollection('')
    },
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DateFnsProvider>
        <Grid container spacing={1}>
          {!hiddenFields.has('originNumberSelectItem') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='originNumberSelectItem'
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    multiple
                    size='small'
                    options={normalizeSelectItems(originOptions || [])}
                    getOptionLabel={getSelectItemOptionLabel}
                    isOptionEqualToValue={isSelectItemEqual}
                    onChange={(e, data) => {
                      const next = normalizeSelectItems(data)
                      onChange(next)
                      formStateRef.current = { ...formStateRef.current, originNumberSelectItem: next }
                      setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                    }}
                    onInputChange={handleInputChangeOrigin}
                    value={value || []}
                    clearOnBlur={false}
                    filterOptions={filterOptions}
                    renderOption={(props, option) => (
                      <li {...props} key={(option as SelectItem).id}>
                        {getSelectItemOptionLabel(option)}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label='Númer uppruna' />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('itemCollectionNumberSelectItem') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='itemCollectionNumberSelectItem'
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    multiple
                    size='small'
                    options={normalizeSelectItems(itemCollectionOptions || [])}
                    getOptionLabel={getSelectItemOptionLabel}
                    isOptionEqualToValue={isSelectItemEqual}
                    onChange={(e, data) => {
                      const next = normalizeSelectItems(data)
                      onChange(next)
                      formStateRef.current = { ...formStateRef.current, itemCollectionNumberSelectItem: next }
                      setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                    }}
                    onInputChange={handleInputChangeItemCollection}
                    value={value || []}
                    clearOnBlur={false}
                    renderOption={(props, option) => (
                      <li {...props} key={(option as SelectItem).id}>
                        {getSelectItemOptionLabel(option)}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label='Númer aðfangs' />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('heiti') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='heiti'
                render={({ field }) => (
                  <TextField
                    {...field}
                    label='Heiti aðfangs'
                    fullWidth
                    size='small'
                  />
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('itemCollectionTypeSelectItem') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='itemCollectionTypeSelectItem'
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    multiple
                    size='small'
                    options={normalizeSelectItems(itemCollectionTypeOptions || [])}
                    getOptionLabel={getSelectItemOptionLabel}
                    isOptionEqualToValue={isSelectItemEqual}
                    onChange={(e, data) => {
                      const next = normalizeSelectItems(data)
                      onChange(next)
                      formStateRef.current = { ...formStateRef.current, itemCollectionTypeSelectItem: next }
                      setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                    }}
                    value={value || []}
                    filterOptions={filterOptions}
                    renderOption={(props, option) => (
                      <li {...props} key={(option as SelectItem).id}>
                        {getSelectItemOptionLabel(option)}
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField {...params} label='Tegund aðfangs' />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('efnislysing') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='efnislysing'
                render={({ field }) => (
                  <Tooltip title='Leitaðu af mörgum orðum með því að setja , á milli orðanna.'>
                    <TextField
                      {...field}
                      label='Lýsing'
                      fullWidth
                      size='small'
                    />
                  </Tooltip>
                )}
              />
            </Grid>
          )}
          {!hiddenFields.has('year') && (
            <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
              <Controller
                control={control}
                name='year'
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    label='Upprunaár'
                    views={['year']}
                    {...register('year')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        error={false}
                        fullWidth
                        size='small'
                      />
                    )}
                    value={value ? new Date(value) : null}
                    onChange={(date) => {
                      if (date) {
                        const yearString = date.getFullYear().toString()
                        if (yearString.length === 4) {
                          onChange(yearString)
                        }
                      } else {
                        onChange(null)
                      }
                    }}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
        <FilterControls
          selected={selected}
          clearFilters={clearFilters}
          register={register}
          deselectCheckboxChange={deselectCheckboxChange}
        />
      </DateFnsProvider>
    </form>
  )
}
